import { useEffect, useState } from "react";

import { ErrorApi, actualizarBrandKit, obtenerBrandKit } from "../api/client";
import type { Asset, BrandKit, ModoBrandKit } from "../api/types";
import type { BrandColors } from "../../../flyer-renderer/src/flyer/types";
import { PaletaColores } from "./PaletaColores";
import estilos from "./PanelBrandKit.module.css";

interface Props {
  token: string;
  proyectoId: string;
  // Lista de assets del proyecto (la trae/mantiene PanelAssets via
  // SeccionMarca): se usa solo para resolver la miniatura del logo actual.
  assets: Asset[];
  // El padre lo incrementa cuando PanelAssets sube/borra un asset tipo
  // "logo" (el backend deriva el kit de marca en el momento de subir, asi
  // que hay que volver a pedirlo).
  disparadorRefresco: number;
}

function mensajeDeError(err: unknown, generico: string): string {
  if (err instanceof ErrorApi) return err.message;
  if (err instanceof Error) return err.message;
  return generico;
}

// Kit de marca (M5b): logo actual, paleta derivada (editable, PATCH manual)
// e interruptor con_marca/estilo_libre. `fonts` se ALMACENA en el backend
// pero es inerte -- flyer-renderer todavia hardcodea Poppins/Inter -- asi que
// este panel deliberadamente no ofrece editar tipografia (evita prometer un
// efecto que hoy no existe).
export function PanelBrandKit({
  token,
  proyectoId,
  assets,
  disparadorRefresco,
}: Props): JSX.Element {
  const [brandKit, setBrandKit] = useState<BrandKit | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const [coloresLocal, setColoresLocal] = useState<BrandColors | null>(null);
  const [guardandoColores, setGuardandoColores] = useState(false);
  const [guardandoModo, setGuardandoModo] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    obtenerBrandKit(token, proyectoId)
      .then((kit) => {
        if (cancelado) return;
        setBrandKit(kit);
        setColoresLocal(kit.colors);
        setErrorCarga(null);
      })
      .catch((err: unknown) => {
        if (!cancelado) {
          setErrorCarga(
            mensajeDeError(err, "No se pudo cargar el kit de marca"),
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [token, proyectoId, disparadorRefresco]);

  const logoAsset =
    assets.find((a) => a.id === brandKit?.logo_asset_id) ?? null;

  if (cargando && !brandKit) {
    return (
      <section className={estilos.tarjeta}>
        <p className={estilos.mensaje}>Cargando kit de marca...</p>
      </section>
    );
  }

  if (errorCarga && !brandKit) {
    return (
      <section className={estilos.tarjeta}>
        <p className={estilos.mensajeError} role="alert">
          {errorCarga}
        </p>
      </section>
    );
  }

  if (!brandKit || !coloresLocal) return <></>;

  const hayCambiosColor =
    JSON.stringify(coloresLocal) !== JSON.stringify(brandKit.colors);

  const guardarColores = async (): Promise<void> => {
    setGuardandoColores(true);
    setErrorGuardado(null);
    try {
      const actualizado = await actualizarBrandKit(token, proyectoId, {
        colors: coloresLocal,
      });
      setBrandKit(actualizado);
      setColoresLocal(actualizado.colors);
    } catch (err) {
      setErrorGuardado(mensajeDeError(err, "No se pudo guardar la paleta"));
    } finally {
      setGuardandoColores(false);
    }
  };

  const cambiarModo = async (siguiente: ModoBrandKit): Promise<void> => {
    if (siguiente === brandKit.mode || guardandoModo) return;
    setGuardandoModo(true);
    setErrorGuardado(null);
    try {
      const actualizado = await actualizarBrandKit(token, proyectoId, {
        mode: siguiente,
      });
      setBrandKit(actualizado);
      setColoresLocal(actualizado.colors);
    } catch (err) {
      setErrorGuardado(mensajeDeError(err, "No se pudo cambiar el modo"));
    } finally {
      setGuardandoModo(false);
    }
  };

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-brand-kit">
      <header className={estilos.encabezado}>
        <h3 id="titulo-brand-kit">Kit de marca</h3>
        <p className={estilos.subtitulo}>
          {brandKit.mode === "con_marca"
            ? "Paleta derivada del logo (contraste garantizado). El flyer generado la usara."
            : "Sin logo con suficientes colores todavia: se usara una paleta neutra por defecto."}
        </p>
      </header>

      <div className={estilos.logoFila}>
        {logoAsset ? (
          <div className={estilos.logoMiniaturaWrap}>
            <img
              src={logoAsset.url}
              alt="Logo de marca actual"
              className={estilos.logoMiniatura}
            />
          </div>
        ) : (
          <p className={estilos.logoVacio}>
            Sin logo subido todavia. Sube uno en "Assets del proyecto" para
            derivar la paleta automaticamente.
          </p>
        )}
      </div>

      <div
        className={estilos.modoFila}
        role="group"
        aria-labelledby="titulo-modo-marca"
      >
        <span id="titulo-modo-marca" className={estilos.modoEtiqueta}>
          Modo
        </span>
        <div className={estilos.interruptor}>
          <button
            type="button"
            className={`${estilos.opcionModo} ${
              brandKit.mode === "con_marca" ? estilos.opcionModoActiva : ""
            }`}
            aria-pressed={brandKit.mode === "con_marca"}
            onClick={() => void cambiarModo("con_marca")}
            disabled={guardandoModo}
          >
            Con marca
          </button>
          <button
            type="button"
            className={`${estilos.opcionModo} ${
              brandKit.mode === "estilo_libre" ? estilos.opcionModoActiva : ""
            }`}
            aria-pressed={brandKit.mode === "estilo_libre"}
            onClick={() => void cambiarModo("estilo_libre")}
            disabled={guardandoModo}
          >
            Estilo libre
          </button>
        </div>
      </div>

      <PaletaColores
        colores={coloresLocal}
        deshabilitado={guardandoColores}
        onCambiar={(campo, valor) =>
          setColoresLocal((prev) => (prev ? { ...prev, [campo]: valor } : prev))
        }
      />

      {errorGuardado && (
        <p className={estilos.mensajeError} role="alert">
          {errorGuardado}
        </p>
      )}

      <div className={estilos.pie}>
        <button
          type="button"
          className="boton boton-primario"
          onClick={() => void guardarColores()}
          disabled={!hayCambiosColor || guardandoColores}
        >
          {guardandoColores ? "Guardando..." : "Guardar paleta"}
        </button>
      </div>
    </section>
  );
}
